import { familiaPorRamoId, FAMILIA_GMM, FAMILIA_VIDA } from '@/utils/familiaSeguro';

export function buildCotizacionSnapshot(store) {
    return {
        _version: '1.1',
        _fechaCaptura: new Date().toISOString(),
        contratante: _buildContratante(store),
        laboral: _buildLaboral(store),
        producto: _buildProducto(store),
        cotizacion: _buildCotizacion(store),
        asegurados: _buildAsegurados(store),
    };
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const FRECUENCIAS = { 24: 'Quincenal', 12: 'Mensual', 4: 'Trimestral', 2: 'Semestral', 1: 'Anual' };
const LUGARES_TRABAJO = { '1': 'Secretaría de Salud', '2': 'Otro' };
const TIPOS_DOMICILIO_FALLBACK = { 1: 'Personal', 2: 'Laboral', 3: 'Fiscal' };

function _ref(catalog, id, nameField = 'nombre') {
    if (id === '' || id == null) return null;
    const found = (catalog || []).find(c => String(c.id) === String(id));
    return { id, nombre: found ? (found[nameField] ?? null) : null };
}

function _tipoMedioId(item) {
    return item?.medioId ?? item?.id ?? item?.tipoMedioId ?? null;
}
function _tipoMedioNombre(catalog, tipoId) {
    if (!catalog || tipoId == null) return null;
    const found = (catalog || []).find(t => String(_tipoMedioId(t)) === String(tipoId));
    return found?.nombre ?? null;
}

// ─── secciones ────────────────────────────────────────────────────────────────

function _buildContratante(store) {
    return {
        id: store.contratanteId || null,
        rfc: store.rfc,
        curp: store.curp,
        nombre: store.nombre,
        paterno: store.paterno,
        materno: store.materno,
        fechaNacimiento: store.fechaNacimiento,
        domicilios: (store.domicilios || []).map(d => ({
            tipo: {
                id: d.tipoId,
                nombre: _ref(store.tiposDomicilios, d.tipoId)?.nombre
                    ?? TIPOS_DOMICILIO_FALLBACK[d.tipoId]
                    ?? null,
            },
            codigoPostal: d.codigoPostal || null,
            estado: d.estado || null,
            estadoId: d.estadoId || null,
            municipio: d.municipio || null,
            municipioId: d.municipioId || null,
            colonia: d.colonia || null,
            calle: d.calle || null,
            ciudad: d.ciudad || null,
            numeroExt: d.numeroExt || null,
            numeroInt: d.numeroInt || null,
            paisId: d.paisId || null,
        })),
        mediosContacto: (store.mediosContacto || []).map(m => ({
            tipo: {
                id: m.tipoMedioId,
                nombre: _tipoMedioNombre(store.tiposMediosContacto, m.tipoMedioId),
            },
            valor: m.descripcion,
        })),
        informacionFinanciera: (store.informacionFinanciera || []).map(f => ({
            instrumento: _ref(store.instrumentos, f.instrumentoId),
            banco: _ref(store.bancos, f.bancoId),
            cuenta: f.cuenta || null,
        })),
    };
}

function _buildLaboral(store) {
    return {
        lugarTrabajo: store.lugarTrabajoId
            ? { id: store.lugarTrabajoId, nombre: LUGARES_TRABAJO[store.lugarTrabajoId] ?? null }
            : null,
        organizacion: _ref(store.organizaciones, store.organizacionId),
        unidad: _ref(store.unidades, store.unidadId),
        referencia: store.referencia || null,
        enroler: _ref(store.enrollers, store.enrolerId),
    };
}

function _buildProducto(store) {
    const divisaFound = (store.divisas || []).find(d => String(d.id) === String(store.divisaId));
    return {
        aseguradora: { id: store.aseguradoraId || null, nombre: store.aseguradoraNombre || null },
        producto: { id: store.productoId || null, nombre: store.productoNombre || null },
        ramo: _ref(store.ramos, store.ramoId),
        plazo: _ref(store.plazos, store.plazoId, 'plazo'),
        tipo: _ref(store.tipos, store.tipoId),
        divisa: store.divisaId
            ? { id: store.divisaId, nombre: divisaFound?.nombre ?? null, simbolo: divisaFound?.simbolo ?? null }
            : null,
    };
}

function _buildCotizacion(store) {
    const pp = store.periodoPago;
    const base = {
        poliza: store.polizaNumero || null,
        periodoPago: pp !== '' && pp != null
            ? { id: pp, nombre: FRECUENCIAS[pp] ?? null }
            : null,
        deducible: store.deducible || null,
        inicioVigencia: store.inicioVigencia || null,
        finVigencia: store.finVigencia || null,
        notasOrdenDeTrabajo: store.notasOrdenDeTrabajo || null,
        observacionesInternas: store.observacionesInternas || null,
        primas: {
            sumaPrimas: store.sumaPrimas ?? 0,
            derechoPoliza: store.derechoPoliza ?? 0,
            iva: store.iva ?? 0,
            montoIva: store.montoIva ?? 0,
            primaTotal: store.primaTotal ?? 0,
            primaPeriodica: store.primaPeriodica ?? 0,
        },
    };

    const extensiones = _buildExtensiones(store);
    if (Object.keys(extensiones).length > 0) {
        base.extensiones = extensiones;
    }

    return base;
}

function _buildAsegurados(store) {
    return (store.asegurados || []).map(a => ({
        id: a.id ?? null,
        nombre: a.nombre,
        paterno: a.paterno,
        materno: a.materno,
        edad: a.edad,
        genero: a.genero,
        parentesco: a.parentesco,
        antiguedad: a.antiguedad ?? null,
        fechaAlta: a.fechaAlta ?? null,
        fechaFin: a.fechaFin ?? null,
        prima: a.prima ?? 0,
    }));
}

// ─── extensiones por producto ─────────────────────────────────────────────────

const _EXTENSIONES = {
    [FAMILIA_GMM]: _extGMM,
    [FAMILIA_VIDA]: _extVida,
};

function _buildExtensiones(store) {
    const builder = _EXTENSIONES[familiaPorRamoId(store.ramoId)];
    return builder ? builder(store) : {};
}

function _extGMM(store) {
    return {
        fechaSolicitud: store.fechaSolicitud || null,
        diasContratados: store.diasContratados ?? null,
        sumaAsegurada: store.sumaAsegurada ?? null,
        coaseguro: store.coaseguro ?? null,
        topeCoaseguro: store.topeCoaseguro ?? null,
    };
}

function _extVida(store) {
    return {
        fechaSolicitud: store.fechaSolicitud || null,
        sumaAsegurada: store.sumaAsegurada ?? null,
    };
}

// ─── DTO de condiciones para el endpoint wizard/poliza ────────────────────────

export function buildCondicionesDto(store) {
    // El tipo de condiciones depende de la familia del seguro (ramo), no del producto.
    const typeKey = familiaPorRamoId(store.ramoId);
    if (!typeKey) return null;
    const freq = store.periodoPago;

    // Campos base — PolizasCondiciones
    const base = {
        '$type': typeKey,
        primaNeta: store.sumaPrimas ?? null,
        iva: store.montoIva ?? null,
        primaTotal: store.primaTotal ?? null,
        frecuenciaPagoId: freq ? Number(freq) : null,
        frecuenciaPago: freq ? (FRECUENCIAS[freq] ?? null) : null,
        primaPeriodica: store.primaPeriodica ?? null,
        inicioVigencia: store.inicioVigencia || null,
        finVigencia: store.finVigencia || null,
    };

    // CondicionesVida
    if (typeKey === 'vida') {
        const primerAsegurado = (store.asegurados || [])[0];
        return {
            ...base,
            fechaAlta: store.inicioVigencia || null,
            fechaBaja: store.finVigencia || null,
            diasContratados: store.diasContratados ? Number(store.diasContratados) : 1,
            sumaAsegurada: store.sumaAsegurada ?? null,
            derechoPoliza: store.derechoPoliza ?? null,
            condicionesAsegurados: primerAsegurado ? {
                aseguradoId: primerAsegurado.aseguradoId ?? null,
                primaAsegurado: primerAsegurado.prima ?? null,
            } : null,
        };
    }

    // CondicionesGMM
    if (typeKey === 'gmm') {
        const asegurados = store.asegurados || [];
        return {
            ...base,
            derechoPoliza: store.derechoPoliza ?? null,
            deducible: store.deducible || null,
            sumaAsegurada: store.sumaAsegurada ?? null,
            coaseguro: store.coaseguro ?? null,
            topeCoaseguro: store.topeCoaseguro ?? null,
            aseguradosIds: asegurados.map(a => a.aseguradoId).filter(id => id != null),
            fechaAntiguedad: asegurados[0]?.antiguedad || null,
            condicionesAsegurados: asegurados.map(a => ({
                aseguradoId: a.aseguradoId ?? null,
                primaAsegurado: a.prima ?? null,
            })),
        };
    }

    return base;
}
